import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown"
import {
  Badge,
  Box,
  FormControlLabel,
  Button as MuiButton,
  Popover,
  Switch,
} from "@mui/material"
import {
  DateCalendar,
  LocalizationProvider,
  MonthCalendar,
  PickersDay,
  PickersDayProps,
  YearCalendar,
} from "@mui/x-date-pickers"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import dayjs, { Dayjs } from "dayjs"
import "dayjs/locale/es"
import { useMemo, useState } from "react"
import {
  Button,
  Card,
  Col,
  OverlayTrigger,
  Row,
  Tooltip,
} from "react-bootstrap"
import { Link } from "react-router-dom"
import { IEventos } from "../../interfaces/IEventos"
import { TipoEvento } from "../../interfaces/TipoEvento"
import { formatDateGuiones, hasEventPassed } from "../../utils/dateUtils"
import { getThemeByTipo } from "../../utils/tipoEventoTheme"
import "./EventosPage.css"

dayjs.locale("es")

const DOT_COLOR: Record<TipoEvento, string> = {
  [TipoEvento.EGRESADOS]: "#4477bb",
  [TipoEvento.CUMPLEANIOS_BODAS]: "#f59533",
  [TipoEvento.FIESTAS]: "#4da352",
}

interface EventosCalendarioViewProps {
  data: IEventos[]
  onAdd?: (fechaDefecto?: string) => void
  onUpdate?: (item: IEventos) => void
  onDelete?: (item: IEventos) => void
  vistaCalendario: boolean
  onToggleVista: (value: boolean) => void
}

function EventosCalendarioView({
  data,
  onAdd,
  onUpdate,
  onDelete,
  vistaCalendario,
  onToggleVista,
}: EventosCalendarioViewProps) {
  const [fechaCalendario, setFechaCalendario] = useState<Dayjs>(dayjs())
  const [monthAnchorEl, setMonthAnchorEl] = useState<HTMLButtonElement | null>(
    null,
  )
  const [yearAnchorEl, setYearAnchorEl] = useState<HTMLButtonElement | null>(
    null,
  )

  // Mapa de eventos por fecha para el calendario
  const eventosPorFecha = useMemo(() => {
    const mapa: Record<string, IEventos[]> = {}
    data.forEach((evento) => {
      const fechaKey = dayjs(evento.fecha).format("YYYY-MM-DD")
      if (!mapa[fechaKey]) {
        mapa[fechaKey] = []
      }
      mapa[fechaKey].push(evento)
    })
    return mapa
  }, [data])

  // Eventos del día seleccionado en el calendario
  const eventosDelDia = useMemo(() => {
    const fechaKey = fechaCalendario.format("YYYY-MM-DD")
    return eventosPorFecha[fechaKey] || []
  }, [fechaCalendario, eventosPorFecha])

  // Componente personalizado para los días del calendario con puntos de color por tipo
  const DiaConBadge = (props: PickersDayProps<Dayjs>) => {
    const { day, outsideCurrentMonth, ...other } = props
    const fechaKey = day.format("YYYY-MM-DD")
    const eventosEnFecha = outsideCurrentMonth
      ? []
      : eventosPorFecha[fechaKey] || []
    const tiposUnicos = [
      ...new Set(eventosEnFecha.map((e) => e.tipo ?? TipoEvento.EGRESADOS)),
    ]
    const hasEvents = !outsideCurrentMonth && tiposUnicos.length > 0

    const badgeContent = hasEvents ? (
      tiposUnicos.length === 1 ? (
        eventosEnFecha.length
      ) : (
        <Box sx={{ display: "flex", gap: "2px", alignItems: "center" }}>
          {tiposUnicos.map((tipo) => {
            const count = eventosEnFecha.filter(
              (e) => (e.tipo ?? TipoEvento.EGRESADOS) === tipo,
            ).length
            return (
              <Box
                key={tipo}
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  backgroundColor: DOT_COLOR[tipo as TipoEvento] ?? "#888",
                  color: "#fff",
                  fontSize: "0.55rem",
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {count}
              </Box>
            )
          })}
        </Box>
      )
    ) : undefined

    const badgeColor =
      hasEvents && tiposUnicos.length === 1
        ? DOT_COLOR[tiposUnicos[0] as TipoEvento]
        : "transparent"

    return (
      <Badge
        key={day.toString()}
        overlap="circular"
        badgeContent={badgeContent}
        sx={{
          "& .MuiBadge-badge": {
            fontSize: "0.65rem",
            height: "16px",
            minWidth: "16px",
            backgroundColor: badgeColor,
            color: "#fff",
            padding: tiposUnicos.length > 1 ? 0 : undefined,
          },
        }}
      >
        <PickersDay
          {...other}
          day={day}
          outsideCurrentMonth={outsideCurrentMonth}
        />
      </Badge>
    )
  }

  return (
    <>
      <Row className="align-items-center mb-3">
        <Col xs={12} className="text-start mb-2">
          <div className="d-flex align-items-center">
            <Button
              variant="primary"
              onClick={() => onAdd?.(fechaCalendario.format("YYYY-MM-DD"))}
              className="me-3 btn-agregar-eventos"
            >
              Agregar Evento <i className="bi bi-plus-square"></i>
            </Button>
            <FormControlLabel
              control={
                <Switch
                  checked={vistaCalendario}
                  onChange={(e) => onToggleVista(e.target.checked)}
                  color="primary"
                />
              }
              label="Modo Calendario"
              labelPlacement="start"
            />
          </div>
        </Col>
      </Row>

      <div className="vista-calendario-wrapper">
        <Row>
          <Col xs={12} md={7} lg={6}>
            <Box className="calendario-container">
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale="es"
              >
                <Box sx={{ position: "relative" }}>
                  <Box
                    sx={{
                      position: "absolute",
                      top: 4,
                      left: 8,
                      zIndex: 1,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <MuiButton
                      variant="text"
                      onClick={(e) => setMonthAnchorEl(e.currentTarget)}
                      endIcon={
                        <ArrowDropDownIcon
                          sx={{
                            color: "rgba(0, 0, 0, 0.54)",
                            transform: monthAnchorEl
                              ? "rotate(180deg)"
                              : "rotate(0deg)",
                            transition: "transform 0.2s",
                          }}
                        />
                      }
                      sx={{
                        textTransform: "capitalize",
                        fontWeight: 600,
                        fontSize: "1.1rem",
                        color: "rgba(0, 0, 0, 0.87)",
                        "&:hover": {
                          backgroundColor: "rgba(0, 0, 0, 0.04)",
                        },
                      }}
                    >
                      {fechaCalendario.format("MMMM")}
                    </MuiButton>
                    <MuiButton
                      variant="text"
                      onClick={(e) => setYearAnchorEl(e.currentTarget)}
                      endIcon={
                        <ArrowDropDownIcon
                          sx={{
                            color: "rgba(0, 0, 0, 0.54)",
                            transform: yearAnchorEl
                              ? "rotate(180deg)"
                              : "rotate(0deg)",
                            transition: "transform 0.2s",
                          }}
                        />
                      }
                      sx={{
                        fontWeight: 600,
                        fontSize: "1.1rem",
                        color: "rgba(0, 0, 0, 0.87)",
                        "&:hover": {
                          backgroundColor: "rgba(0, 0, 0, 0.04)",
                        },
                      }}
                    >
                      {fechaCalendario.format("YYYY")}
                    </MuiButton>
                  </Box>
                  <Popover
                    open={Boolean(monthAnchorEl)}
                    anchorEl={monthAnchorEl}
                    onClose={() => setMonthAnchorEl(null)}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "center",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "center",
                    }}
                  >
                    <MonthCalendar
                      value={fechaCalendario}
                      onChange={(newValue) => {
                        if (newValue) {
                          setFechaCalendario(newValue)
                          setMonthAnchorEl(null)
                        }
                      }}
                    />
                  </Popover>
                  <Popover
                    open={Boolean(yearAnchorEl)}
                    anchorEl={yearAnchorEl}
                    onClose={() => setYearAnchorEl(null)}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "center",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "center",
                    }}
                  >
                    <YearCalendar
                      value={fechaCalendario}
                      onChange={(newValue) => {
                        if (newValue) {
                          setFechaCalendario(newValue)
                          setYearAnchorEl(null)
                        }
                      }}
                    />
                  </Popover>
                  <DateCalendar
                    value={fechaCalendario}
                    onChange={(newValue) =>
                      newValue && setFechaCalendario(newValue)
                    }
                    onMonthChange={(newMonth) => setFechaCalendario(newMonth)}
                    slots={{
                      day: DiaConBadge,
                    }}
                    sx={{
                      width: "100%",
                      maxWidth: "100%",
                      "& .MuiPickersCalendarHeader-root": {
                        paddingLeft: "16px",
                        paddingRight: "16px",
                        justifyContent: "flex-end",
                      },
                      "& .MuiPickersCalendarHeader-labelContainer": {
                        display: "none",
                      },
                      "& .MuiPickersArrowSwitcher-root": {
                        position: "relative",
                      },
                      "& .MuiDayCalendar-weekContainer": {
                        justifyContent: "space-around",
                      },
                      "& .MuiPickersDay-root": {
                        width: "40px",
                        height: "40px",
                        fontSize: "1rem",
                      },
                      "& .MuiDayCalendar-header": {
                        justifyContent: "space-around",
                      },
                      "& .MuiDayCalendar-weekDayLabel": {
                        width: "40px",
                        fontSize: "0.9rem",
                      },
                    }}
                  />
                </Box>
              </LocalizationProvider>
            </Box>
          </Col>
          <Col xs={12} md={5} lg={6}>
            <div className="eventos-calendario-header mb-3">
              <h5 className="mb-0">
                Eventos del {fechaCalendario.format("DD/MM/YYYY")}
                {eventosDelDia.length > 0 && (
                  <span className="badge bg-primary ms-2">
                    {eventosDelDia.length}
                  </span>
                )}
              </h5>
            </div>
            <div className="eventos-calendario-lista">
              {eventosDelDia.length === 0 ? (
                <p className="text-muted">No hay eventos para esta fecha</p>
              ) : (
                eventosDelDia.map((item, index) => (
                  <Card
                    key={index}
                    className={
                      !hasEventPassed(item.fecha)
                        ? "card-eventos-calendario mb-2"
                        : "card-eventos-calendario-passed mb-2"
                    }
                    style={
                      !hasEventPassed(item.fecha)
                        ? {
                            ["--card-bg" as string]: getThemeByTipo(item.tipo).card,
                            ["--card-border" as string]: getThemeByTipo(item.tipo).border,
                            ["--card-hover-bg" as string]: getThemeByTipo(item.tipo).cardHover,
                          }
                        : undefined
                    }
                  >
                    <Card.Body className="d-flex align-items-center justify-content-between py-2 px-3">
                      <Link
                        to={`/egresados/${item.id}`}
                        className="link-card-calendario flex-grow-1"
                      >
                        <div className="d-flex align-items-center">
                          <Card.Title
                            className={
                              !hasEventPassed(item.fecha)
                                ? "card-title mb-0 me-3"
                                : "card-title-passed mb-0 me-3"
                            }
                          >
                            {item.nombre}
                          </Card.Title>
                          <Card.Text
                            className={
                              !hasEventPassed(item.fecha)
                                ? "card-title mb-0 text-muted"
                                : "card-title-passed mb-0"
                            }
                            style={{ fontSize: "0.9rem" }}
                          >
                            {formatDateGuiones(item.fecha)}
                          </Card.Text>
                        </div>
                      </Link>
                      <div className="d-flex" style={{ gap: "8px" }}>
                        {onUpdate && (
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id="tooltip-editar">Editar</Tooltip>
                            }
                          >
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation()
                                onUpdate(item)
                              }}
                            >
                              <i className="bi bi-pencil-square"></i>
                            </Button>
                          </OverlayTrigger>
                        )}
                        {onDelete && (
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id="tooltip-eliminar">Eliminar</Tooltip>
                            }
                          >
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation()
                                onDelete(item)
                              }}
                            >
                              <i className="bi bi-trash"></i>
                            </Button>
                          </OverlayTrigger>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                ))
              )}
            </div>
          </Col>
        </Row>
      </div>
    </>
  )
}

export default EventosCalendarioView
